import { useMemo } from "react";
import { useFormikContext } from "formik";
import { TaskCreate } from "@common/contracts/task.contract";
import {
    InvoiceView,
    InvoiceViewSchema,
} from "@common/types/views/invoiceViews";
import { PersistableView } from "@common/types/views/views";
import { WORKSPACE_INVOICES_FILTER_TABS } from "@components/tasks/TaskBody/Invoices/invoicesTaskFilters";
import { buildInvoicesTaskFilters } from "@components/tasks/TaskBody/Invoices/invoicesTaskQuery";
import { InvoicesTaskTable } from "@components/tasks/TaskBody/Invoices/InvoicesTaskTable";
import { parseInvoicesTaskView } from "@components/tasks/TaskBody/Invoices/invoicesTaskView";
import { withoutBulkActions } from "@components/tasks/TaskBody/shared/taskTableView";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import styles from "./CreateTaskDrawer.module.scss";
import { TaskCreationEntityFilter } from "./TaskCreationEntityFilter";
import { useTaskCreationPreviewView } from "./useTaskCreationPreviewView";

interface TaskCreationInvoicesPreviewProps {
    viewConfig: PersistableView;
}

interface PreviewBodyProps {
    initialView: InvoiceView;
    isSelection: boolean;
}

const renderEmptyPreview = () => (
    <EmptyState header="No invoices match this task" />
);

const PreviewBody = ({ initialView, isSelection }: PreviewBodyProps) => {
    const { view, handleFiltersChange, handleViewChange } =
        useTaskCreationPreviewView({
            initialView,
            viewSchema: InvoiceViewSchema,
        });

    const { values } = useFormikContext<TaskCreate>();
    const entityIds = isSelection ? view.filters.entityIds : values.entityIds;

    const availableFilters = isSelection ? [] : WORKSPACE_INVOICES_FILTER_TABS;
    const apiFilters = useMemo(
        () =>
            buildInvoicesTaskFilters({
                viewFilters: {
                    ...view.filters,
                    entityIds: entityIds?.length ? entityIds : undefined,
                },
            }),
        [view.filters, entityIds],
    );

    return (
        <InvoicesTaskTable
            view={view}
            onViewChange={handleViewChange}
            onFiltersChange={handleFiltersChange}
            availableFilters={availableFilters}
            defaultView={initialView}
            apiFilters={apiFilters}
            headerActions={
                isSelection ? undefined : <TaskCreationEntityFilter />
            }
            emptyView={renderEmptyPreview}
        />
    );
};

/**
 * Left pane of the create task drawer for an INVOICES task: the invoices table
 * the task will show. Filters, search and columns edited here are written back
 * to the form's `view`. A task created from an invoices selection shows exactly
 * the selected invoices.
 */
export const TaskCreationInvoicesPreview = ({
    viewConfig,
}: TaskCreationInvoicesPreviewProps) => {
    const initialView = useMemo(
        () => withoutBulkActions(parseInvoicesTaskView(viewConfig)),
        [viewConfig],
    );
    const isSelection = !!viewConfig.filters.invoiceIds?.length;

    return (
        <div className={styles.previewPane}>
            <div className={styles.previewSurface}>
                <PreviewBody
                    initialView={initialView}
                    isSelection={isSelection}
                />
            </div>
        </div>
    );
};
